import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  Optional,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { CookieService } from 'ngx-cookie-service';

import { RedirectionService } from '@shared/services/redirection.service';

import { environment } from '@environments/environment';

import { WebcomponentsService } from '@core/service/webcomponents.service';

import { UpdateDetailRequestType } from '@core/model';
import { SimulationType, SimulatorType, SimulatorPropertiesType } from './models';

import { ALLIANCE_TYPE_ENUMERATION, LOAN_STATE_ENUMERATION, SIMULATION_PROPERTIES_ENUMERATION } from './config';
import { ProductType } from '@core/model/enums';

@Component({
  selector: 'app-simulator',
  templateUrl: './simulator.component.html',
  styleUrls: ['./simulator.component.css'],
})
export class SimulatorComponent implements AfterViewInit, OnDestroy {
  @Input() public properties: SimulatorPropertiesType;

  @ViewChild('simulatorRef') public simulatorRef: ElementRef;
  public simulatorRefInstance: SimulatorType;

  private readonly updateDetailInfoRequest: UpdateDetailRequestType;
  private readonly domain = environment.domain;
  public landingRapiplazo: string;

  private readonly webElementService: WebcomponentsService;
  private readonly cookieService: CookieService;
  private readonly redirect: RedirectionService;

  private readonly unSubscribe$: Subject<void>;

  public simulatorIsLoading: boolean;

  constructor(
    private readonly injector: Injector,
    private readonly ngZone: NgZone,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly data: SimulatorPropertiesType,
    @Optional() public dialogRef: MatDialogRef<SimulatorComponent>
  ) {
    this.properties = {} as SimulatorPropertiesType;
    this.setInputValues();
    this.landingRapiplazo = environment.landingRapiplazo;
    this.webElementService = this.injector.get(WebcomponentsService);
    this.cookieService = this.injector.get(CookieService);
    this.redirect = this.injector.get(RedirectionService);

    this.updateDetailInfoRequest = {} as UpdateDetailRequestType;
    this.unSubscribe$ = new Subject();
    this.simulatorIsLoading = true;
    this.simulatorRefInstance = {} as SimulatorType;
  }

  public ngAfterViewInit(): void {
    this.initialize();
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    if (this.simulatorRefInstance) {
      this.simulatorRefInstance.destroySimulator();
    }
  }

  public get isAnUpdate(): boolean {
    return this.properties.type === ALLIANCE_TYPE_ENUMERATION.UPDATE;
  }

  public loadSimulatorRef({ amount, term }: { amount?: number; term?: number }): void {
    this.webElementService.isLoaded$.pipe(filter(Boolean), take(1)).subscribe({
      next: () => {
        const previousSimulation = this.getPreviousSimulationByProduct(amount, term);
        const simulatorConfigurationObj = {
          productCode: ProductType.RP,
          previousSimulationByProduct: previousSimulation,
          runAfterLoading: (simulationOutput: SimulationType) => {
            if (simulationOutput?.product == ProductType.RP && !this.isAnUpdate) {
              this.setCookiesAmountAndTerm();
            }
          },
          runWhenLoadingItems: (isLoadingSimulate: boolean) => {
            this.ngZone.run(() => {
              this.simulatorIsLoading = isLoadingSimulate;
            });
          },
        };

        this.simulatorRefInstance = this.simulatorRef.nativeElement._ngElementStrategy?.componentRef.instance;
        this.simulatorRefInstance.buildSimulator(simulatorConfigurationObj);
      },
    });
  }

  private getPreviousSimulationByProduct(amountValue: number, timeValue: number): { RP: SimulationType } | null {
    if (amountValue === 0 || timeValue === 0) {
      return null;
    }

    return { RP: { amountValue, timeValue } };
  }

  public initialize(): void {
    const amount = parseInt(this.requestedAmount);
    const term = parseInt(this.requestedTerm);
    this.loadSimulatorRef({ amount, term });
  }

  private redirectToProductValidations(): void {
    this.setCookiesAndStorage(LOAN_STATE_ENUMERATION.NEW, 'true');
    this.redirect.navigateTo('/productValidations');
  }

  private redirectToEmailVerification(): void {
    this.setCookiesAndStorage(LOAN_STATE_ENUMERATION.NEW, 'true');
    this.redirect.navigateTo('account/emailVerification');
  }

  public submit(): void {
    if (this.properties.type === ALLIANCE_TYPE_ENUMERATION.UPDATE) {
      this.typeIsAnUpdate();
    } else {
      this.redirectToProductValidations();
    }
  }

  private typeIsAnUpdate(): void {
    this.setCookiesAmountAndTerm();

    this.dialogRef?.close({
      amount: this.requestedAmount,
      term: this.requestedTerm,
      monthFeeValue: this.quotas,
    });
  }

  private get requestedAmount(): string {
    return (
      this.cookieService.get(SIMULATION_PROPERTIES_ENUMERATION.AMOUNT) ||
      sessionStorage.getItem(SIMULATION_PROPERTIES_ENUMERATION.AMOUNT)
    );
  }

  private get requestedTerm(): string {
    return (
      this.cookieService.get(SIMULATION_PROPERTIES_ENUMERATION.TERM) ||
      sessionStorage.getItem(SIMULATION_PROPERTIES_ENUMERATION.TERM)
    );
  }

  private get quotas(): string {
    return (
      this.cookieService.get(SIMULATION_PROPERTIES_ENUMERATION.QUOTA) ||
      sessionStorage.getItem(SIMULATION_PROPERTIES_ENUMERATION.QUOTA)
    );
  }

  private setInputValues(): void {
    this.properties.buttonLabel = 'PIDE TU RAPIPLAZO YA';
    if (this.data) {
      this.properties.buttonLabel = this.data.buttonLabel;
      this.properties.type = this.data.type;
    }
  }

  private setCookiesAmountAndTerm(): void {
    if (this.simulatorRefInstance?.getSimulation()) {
      const { amountValue, timeValue, items } = this.simulatorRefInstance.getSimulation();
      const fees = items?.reduce((acc, item) => acc + item.value, 0);
      this.setCookiesAndStorage(SIMULATION_PROPERTIES_ENUMERATION.AMOUNT, amountValue);
      this.setCookiesAndStorage(SIMULATION_PROPERTIES_ENUMERATION.TERM, timeValue);
      this.setCookiesAndStorage(SIMULATION_PROPERTIES_ENUMERATION.QUOTA, fees);
    }
  }

  private setCookiesAndStorage(key: string, value: number | string | Date): void {
    this.cookieService.set(key, value.toString(), 1, '/', this.domain);
    sessionStorage.setItem(key, value.toString());
  }
}
